import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';

import { clientGraphQL } from '../../api/Client';
import { sortBydate } from '../../components/hoc/SortField';
import useComponentState from '../../utils/useComponentState';
import { storeDashboardDetails } from '../../actions/dashboard';
import { Loading, PageHeader, EarningItem } from '../../components';
import Graph from '../../components/graph/Graph';

import './ClientDashboard.scss';
import { formatDateTime } from '../../utils/dataFormatter';
import { DATE_TIME_TYPES } from '../../constants/dataTimeTypes';

const CLIENT_VOUCHER_TYPE_MAP = {
    AIA: 'AIA_REWARDS',
    NDB: 'NDB_REWARDS',
};

const ClientDashboard = (props) => {
    const [state, setState] = useComponentState({
        dashboard: null,
        dashboardLoading: true,
        dashboardError: null,
        isModalOpen: false,
        modalGraphData: [],
        modalGraphLoading: false,
        modalGraphError: null,
        graphType: null,
        modalFilters: {
            period: 'month',
            fromDate: null,
            toDate: null,
        },
    });

    const {
        auth: { token },
    } = props;
    const { dashboard, dashboardLoading, dashboardError } = state;

    const authToken = props.auth?.token;
    const authClientName =
        props.auth?.details?.clientDetails?.clientName ||
        props.auth?.details?.clientName;
    const userClientName = props.user?.currentUser?.clientName;
    const clientDisplayName = authClientName || userClientName;

    const mapClientToVoucherType = (clientName) => {
        if (!clientName) {
            return null;
        }

        const normalized = String(clientName).trim().toUpperCase();
        return CLIENT_VOUCHER_TYPE_MAP[normalized] || normalized;
    };

    const getVoucherTypeFromUserDetails = () => {
        return mapClientToVoucherType(clientDisplayName);
    };

    useEffect(() => {
        if (!authToken) {
            setState({
                dashboardLoading: false,
                dashboardError: null,
            });
            return;
        }

        handleGetDashboardFromAPI();
    }, [authToken, authClientName, userClientName]);

    const handleGetDashboardFromAPI = async () => {
        if (!authToken) {
            setState({
                dashboardLoading: false,
                dashboardError: 'Client details are not available yet.',
            });
            return;
        }

        try {
            const voucherType = getVoucherTypeFromUserDetails();
            const voucherTypeFilter = voucherType
                ? `{ key: "voucher.type", value: "${voucherType}" },`
                : '';

            const query = `{
            uniqueRedemptionUsersCount(
                filters: {
                    filters: [
                        ${voucherTypeFilter}
                        { key: "status", value: "complete"}
                    ]
                }
                ) {
                userCount
            }
            totalRedemptionAmount(
                filters: {
                    filters: [
                        ${voucherTypeFilter}
                        { key: "status", value: "complete"}
                    ]
                }
                ) {
                totalAmount
            }
            redemptionList(
                    filters: {
                        filters: [
                            ${voucherTypeFilter}
                            { key: "status", value: "complete" },
                        ]
                    }, 
                    page: 0, 
                    size: 10,
                    sortBy: "timestamp",
                    sortDir: "DESC"
                    ) {
                        pageNo
                        totalPageCount
                        list {
                            id
                            amount
                            timestamp
                            status
                            merchant {
                                id
                                name
                            }
                            voucher {
                                id
                                type
                                receiverName
                                mobileNo
                            }
                        }
                    }
            }`;
            setState({ dashboardLoading: true, dashboardError: null });
            const response = await clientGraphQL(query, authToken);
            setState({ dashboardLoading: false, dashboard: { ...response?.data } });
            props.storeDashboardDetails(response);
        } catch (err) {
            setState({
                dashboardLoading: false,
                dashboardError: err?.message || 'Failed to load dashboard',
            });
        }
    };

    const renderLoading = () => (
        <Loading text='Loading Dashboard' />
    );

    const renderError = () => (
        <div className='client_dashboard_error'>
            <p>{dashboardError}</p>
            <button onClick={handleGetDashboardFromAPI}>Retry</button>
        </div>
    );

    const renderMetricsCards = () => {
        if (!dashboard) return null;

        const metrics = [
            {
                label: 'Total Redemptions (LKR)',
                value: dashboard?.totalRedemptionAmount?.totalAmount?.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'LKR'
                }) || 'LKR 0.00',
                icon: '💰',
                graphType: 'getRedemptionEarningGraphData',
            },
            {
                label: 'Users Count',
                value: dashboard?.uniqueRedemptionUsersCount?.userCount || 0,
                icon: '👥',
                graphType: 'getRedemptionUserGraphData',
            },
        ];

        return (
            <div className='client_metrics_grid'>
                {metrics.map((metric, idx) => (
                    <div key={idx} className='metric_card'>
                        <div className='metric_icon'>{metric.icon}</div>
                        <FontAwesomeIcon 
                            icon={faChartBar} 
                            className='metric_chart_icon' 
                            onClick={() => handleOpenModal(metric.graphType)} 
                            title="View Graph"
                        />
                        <div className='metric_label'>{metric.label}</div>
                        <div className='metric_value'>{metric.value}</div>
                    </div>
                ))}
            </div>
        );
    };

    const renderRecentRedemptions = () => {
        if (!dashboard?.redemptionList?.list || dashboard.redemptionList.list.length === 0) {
            return (
                <div className='client_empty_state'>
                    <p>No redemptions yet</p>
                </div>
            );
        }

        const redemptions = dashboard.redemptionList.list;
        sortBydate(redemptions);

        return (
            <div className='client_redemptions_container'>
                <div className='redemptions_header'>
                    <span>Date</span>
                    <span>Redemption ID</span>
                    <span>Mobile No</span>
                    <span>User</span>
                    <span>Amount (LKR)</span>
                    <span>Status</span>
                </div>
                <div className='redemptions_list'>
                    {redemptions.map((redemption, idx) => (
                        <div key={idx} className='redemption_row'>
                            <span>{formatDateTime(redemption.timestamp, DATE_TIME_TYPES.DATE_TIME_12H)}</span>
                            <span className='redemption_id'>{redemption.id}</span>
                            <span>{redemption.voucher?.mobileNo}</span>
                            <span>{redemption.voucher?.receiverName}</span>
                            <span className='amount'>LKR {redemption.amount?.toFixed(2)}</span>
                            <span className={`status status_${redemption.status?.toLowerCase()}`}>
                                {redemption.status}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const handleFetchModalGraphData = async (graphType) => {
        if (!authToken) return;
        setState({ modalGraphLoading: true, modalGraphError: null });
        try {
            const voucherType = getVoucherTypeFromUserDetails();
            const fromZoned = formatDateTime(state.modalFilters.fromDate, DATE_TIME_TYPES.ISO_DATE);
            const toZoned = formatDateTime(state.modalFilters.toDate, DATE_TIME_TYPES.ISO_DATE);
            
            const queryFilters = [
                { key: "period", value: state.modalFilters.period },
                { key: "status", value: "COMPLETE" },
                { key: "voucher.type", value: voucherType }
            ];

            if (state.modalFilters.fromDate && state.modalFilters.toDate) {
                queryFilters.push({ key: "timestamp", from: fromZoned, to: toZoned });
            }

            const query = `{
            ${graphType}(filters: { 
                filters: [${queryFilters.map(f => f.value ? `{ key: "${f.key}", value: "${f.value}" }` : `{ key: "${f.key}", from: "${f.from}", to: "${f.to}" }`).join(', ')}]
            }) {
                x
                y
            }
        }`;

            const response = await clientGraphQL(query, authToken);
            setState({ modalGraphLoading: false, modalGraphData: response?.data?.[graphType] || [] });
        } catch (err) {
            setState({
                modalGraphLoading: false,
                modalGraphError: err?.message || 'Failed to load graph data',
            });
        }
    };

    const getGraphTitle = () => {
        switch(state.graphType) {
            case 'getRedemptionEarningGraphData':
                return 'Redemption Amount';
            case 'getRedemptionUserGraphData':
                return 'Redemption Users';
            default:
                return 'Graph';
        }
    }

    const handleOpenModal = (graphType) => {
        setState({ isModalOpen: true, graphType: graphType });
        handleFetchModalGraphData(graphType);
    };

    const handleCloseModal = () => {
        setState({
            isModalOpen: false,
            modalFilters: {
                period: 'month',
                fromDate: null,
                toDate: null,
            },
        });
    };

    const handleModalFilterChange = (key, value) => {
        setState(prev => ({
            modalFilters: { ...prev.modalFilters, [key]: value }
        }));
    };

    const handleApplyFilters = () => {
        handleFetchModalGraphData(state.graphType);
    };

    const graphDataCleanup = () => {
        const graphKey = state.graphType === 'getRedemptionEarningGraphData' ? 'LKR' : 'Users';
        if (!state.modalGraphData) return [];
        return state.modalGraphData.map(item => ({
            x: item.x,
            [graphKey]: item.y,
        }));
    };

    return (
        <div className='client_dashboard_root'>
            <div className='client_dashboard_header'>
                <h1>{clientDisplayName ? `${clientDisplayName} Rewards` : 'Dashboard'}</h1>
                <p>Welcome back! Here's your redemption overview.</p>
            </div>

            {dashboardLoading ? (
                renderLoading()
            ) : dashboardError ? (
                renderError()
            ) : (
                <div className='client_dashboard_content'>
                    {renderMetricsCards()}

                    <div className='client_redemptions_section'>
                        <PageHeader text='Recent Redemptions' />
                        {renderRecentRedemptions()}
                    </div>
                </div>
            )}

            {state.isModalOpen && (
                <div className="custom-modal-backdrop" onClick={handleCloseModal}>
                    <div className="custom-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="custom-modal-header">
                            <h3>Redemption Trends Graph</h3>
                            <button className="custom-modal-close" onClick={handleCloseModal}>×</button>
                        </div>
                        <div className="custom-modal-body">
                            <div className="modal-filters">
                                <div className="modal-filters-row">
                                    <label>
                                        Period:
                                        <select value={state.modalFilters.period} onChange={(e) => handleModalFilterChange('period', e.target.value)}>
                                            <option value="week">Weekly</option>
                                            <option value="month">Monthly</option>
                                        </select>
                                    </label>
                                </div>
                                <div className="modal-filters-row">
                                    <label>
                                        From Date:
                                        <DatePicker
                                            selected={state.modalFilters.fromDate}
                                            onChange={(date) => handleModalFilterChange('fromDate', date)}
                                            dateFormat="yyyy-MM-dd"
                                        />
                                    </label>
                                    <label>
                                        To Date:
                                        <DatePicker
                                            selected={state.modalFilters.toDate}
                                            onChange={(date) => handleModalFilterChange('toDate', date)}
                                            dateFormat="yyyy-MM-dd"
                                        />
                                    </label>
                                </div>
                                <div className="modal-filters-row modal-filters-actions">
                                    <button
                                        className="apply-filters-btn"
                                        onClick={handleApplyFilters}
                                        disabled={state.modalGraphLoading}
                                    >
                                        {state.modalGraphLoading ? 'Loading...' : 'Apply Filters'}
                                    </button>
                                </div>
                            </div>
                            <Graph 
                                data={graphDataCleanup()} 
                                title={getGraphTitle()} 
                                loading={state.modalGraphLoading} 
                                error={state.modalGraphError}
                                isIntegerYAxis={state.graphType === 'getRedemptionUserGraphData'}
                                dataKey={state.graphType === 'getRedemptionEarningGraphData' ? 'LKR' : 'Users'}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        auth: state.auth.auth,
        dashboard: state.dashboard,
        user: state.user,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        storeDashboardDetails: (data) => {
            dispatch(storeDashboardDetails(data));
        },
    };
};

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(ClientDashboard)
);